'use client'
import React from 'react'
import { Badge, Flex, Text, Title } from '@mantine/core'
import { useContext } from 'react'

import { AvatarSizes, SafeAvatarImage } from '~/app/_components'
import { AppLayoutContext } from '~/app/_components/context'
import { AccountTier, AccountType, ConnectionState, TrimmedAccountFragment } from '~/generated/graphql'
import { accountProfessions, accountType } from '~/utils'

import { getPronoun } from '../_lib'
import { SocialInfo } from '../accounts/[id]/[[...pageParams]]/_components'
import { ProBadge } from './desktop'

export type AccountAvatarVariant = 'community' | 'profile'

// NOTE: we only use this component on mobile as of now,
// but leaving this if we will reuse also on desktop later on
const AVATAR_SIZES: Record<AccountAvatarVariant, Record<'mobile' | 'desktop', AvatarSizes>> = {
  community: {
    mobile: 'xs',
    desktop: 'xs',
  },
  profile: {
    mobile: 'md',
    desktop: 'xl',
  },
}

interface ProfileTextProps {
  children: React.ReactNode
  variant: AccountAvatarVariant
  size?: 'md' | '13px'
  uppercase?: boolean
  fontWeight?: number
}

const ProfileText = ({ children, variant, size = 'md', uppercase = false, fontWeight }: ProfileTextProps) => (
  <Text
    size={size}
    tt={uppercase ? 'uppercase' : undefined}
    fw={fontWeight}
    lh={variant === 'community' ? '15px' : '12px'}
    lts={variant === 'profile' ? '-0.4px' : undefined}
  >
    {children}
  </Text>
)

interface ProfileTitleProps {
  name: string
  variant: AccountAvatarVariant
  isMobile: boolean
}

const ProfileTitle = ({ name, variant, isMobile }: ProfileTitleProps) => {
  const testId = `${variant}-account-title`
  if (variant === 'community') {
    return (
      <Title order={4} tt="uppercase" data-testid={testId}>
        {name}
      </Title>
    )
  }

  if (isMobile) {
    return (
      <Title tt="uppercase" order={2} data-testid={testId}>
        {name}
      </Title>
    )
  }

  const splitName = name.split(' ')
  return (
    <Flex direction="column">
      {splitName.map((name, index) => (
        <Title key={index} tt="uppercase" order={2} lh="119%" data-testid={testId}>
          {name}
        </Title>
      ))}
    </Flex>
  )
}

interface AccountAvatarBlockProps {
  account: TrimmedAccountFragment
  variant: AccountAvatarVariant
}

export function AccountAvatarBlock({ account, variant }: AccountAvatarBlockProps) {
  const { mobile } = useContext(AppLayoutContext)
  // const { currentAccount } = useContext(IdentityContext)
  // const isCurrentUserProfile = currentAccount?.id === account?.id

  const showConnectionBadge = account.connectionStatus === ConnectionState.Connected && variant === 'profile'
  const showPronoun = variant === 'profile' && account.profile?.pronoun
  // NOTE: we never need to render connectbutton from this component as it is now
  // const showConnectButton =
  //   account.connectionStatus !== ConnectionState.Connected && !isCurrentUserProfile && variant === 'profile'

  const renderProfileInfo = () => {
    if (!account.profile) return null

    return (
      <>
        {account.type !== AccountType.Personal && (
          <ProfileText variant={variant} uppercase fontWeight={600} size="13px">
            {accountType(account)}
          </ProfileText>
        )}

        {account.type === AccountType.Personal && account.professions?.length && (
          <ProfileText
            variant={variant}
            uppercase={variant === 'profile'}
            fontWeight={variant === 'community' ? undefined : 600}
            size={variant === 'community' ? 'md' : '13px'}
          >
            {accountProfessions(account)}
          </ProfileText>
        )}

        {account.profile.location && (
          <ProfileText
            variant={variant}
            uppercase={variant === 'profile'}
            fontWeight={variant === 'community' ? undefined : 600}
            size={variant === 'community' ? 'md' : '13px'}
          >
            {account.profile.location}
          </ProfileText>
        )}
      </>
    )
  }

  return (
    <Flex direction="column" gap="md" w="100%">
      <Flex align={mobile ? 'flex-start' : 'center'} gap={mobile ? 'sm' : 'xl'} w="100%">
        <Flex direction="column" align="center" gap="3px">
          <SafeAvatarImage radius={AVATAR_SIZES[variant][mobile ? 'mobile' : 'desktop']} account={account} />
          {account.tier === AccountTier.Pro && <ProBadge />}
        </Flex>
        <Flex direction="column" gap={mobile && variant === 'community' ? '0px' : '5px'} w="100%">
          {(showConnectionBadge || showPronoun) && (
            <Flex gap="md">
              {showConnectionBadge && (
                <Badge size="xs" tt="uppercase" data-testid="connected-badge" fz="xs" color="gray.2" mb="5px">
                  Connected
                </Badge>
              )}
              {showPronoun && account.profile?.pronoun && (
                <Text c="dimmed" size="md">
                  {getPronoun(account.profile.pronoun)}
                </Text>
              )}
            </Flex>
          )}

          <Flex gap="xl" justify="space-between" mb="2px" align="flex-start">
            <Flex w="100%">
              <Flex gap="sm" justify="space-between" w="100%">
                <ProfileTitle name={account.profile?.name || ''} variant={variant} isMobile={mobile} />
                {/* {showConnectButton && <ConnectButton preset="community" />} */}
              </Flex>
            </Flex>
          </Flex>

          {renderProfileInfo()}

          {mobile && variant === 'profile' && <SocialInfo account={account} />}
        </Flex>
      </Flex>
    </Flex>
  )
}
